import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';

function mapSupabaseError(message) {
  if (!message) return 'An unexpected error occurred. Please try again.';
  const m = message.toLowerCase();

  // 🚀 SENSORI PER SAFARI E BLOCCHI DI RETE
  if (m.includes('failed to fetch') || m.includes('network error') || m.includes('load failed')) {
    return 'Safari is blocking the connection (Tracking Prevention). Please use Chrome, or disable "Hide IP Address" in Safari settings.';
  }
  
  // 🍎 iOS Safari: errori specifici del redirect OAuth
  if (m.includes('cancelled') || m.includes('canceled') || m.includes('dismissed')) {
    return 'Login cancelled. Please try again.';
  }

  if (m.includes('popup') || m.includes('blocked')) {
    return 'Popup blocked by browser. Redirecting you to Google login…';
  }

  if (m.includes('body stream already read') || m.includes('unexpected end of json')) {
    return 'Incorrect email or password. Please check your details.';
  }
  if (m.includes('user already registered') || m.includes('already been registered')) {
    return 'This email is already registered. Try signing in instead.';
  }
  if (m.includes('invalid login credentials') || m.includes('invalid credentials')) {
    return 'Incorrect email or password. Please check your details.';
  }
  if (m.includes('email not confirmed')) {
    return 'Please confirm your email address before signing in. Check your inbox.';
  }
  if (m.includes('password should be at least') || m.includes('weak password')) {
    return 'Password is too weak. Use at least 6 characters.';
  }
  if (m.includes('invalid email') || m.includes('unable to validate email')) {
    return 'Please enter a valid email address.';
  }
  return message;
}

function validateSignUpForm(email, password) {
  if (!email.trim()) return 'Email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address.';
  if (!password) return 'Password is required.';
  if (password.length < 6) return 'Password must be at least 6 characters.';
  return null;
}

export default function AuthModal({ open, onOpenChange }) {
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();

  const [signInLoading, setSignInLoading] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false); // 🚀 Stato loading Google

  const [signInError, setSignInError] = useState('');
  const [signUpError, setSignUpError] = useState('');
  
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [signUpData, setSignUpData] = useState({ email: '', password: '' });
  const [resetEmail, setResetEmail] = useState('');

  const reset = () => {
    setSignInError(''); setSignUpError('');
    setSignUpSuccess(false); setIsResetting(false); setResetSuccess(false);
    setSignInData({ email: '', password: '' }); setSignUpData({ email: '', password: '' });
    setResetEmail('');
    setGoogleLoading(false); // 🚀 Reset anche lo stato Google
  };

  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSignInError(''); setSignInLoading(true);
    try {
      const { error } = await signIn(signInData.email, signInData.password);
      if (error) throw error; 
      toast.success('Welcome back!');
      reset();
      onOpenChange(false);
    } catch (err) { 
      setSignInError(mapSupabaseError(err.message || err.toString())); 
    } 
    finally { setSignInLoading(false); }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setSignUpError(''); setSignUpSuccess(false);

    const validationError = validateSignUpForm(signUpData.email, signUpData.password);
    if (validationError) { setSignUpError(validationError); return; }

    setSignUpLoading(true);
    try {
      const { data, error } = await signUp(signUpData.email, signUpData.password);
      if (error) throw error; 
      if (data?.session) {
        toast.success('Account created! Welcome 🎉');
        reset(); onOpenChange(false);
      } else {
        setSignUpSuccess(true);
      }
    } catch (err) { 
      setSignUpError(mapSupabaseError(err.message || err.toString())); 
    } 
    finally { setSignUpLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) { setSignInError('Please enter your email.'); return; }
    setResetLoading(true); setSignInError('');
    try {
      const { error } = await resetPassword(resetEmail);
      if (error) throw error;
      setResetSuccess(true);
    } catch (err) { 
      setSignInError(mapSupabaseError(err.message || err.toString())); 
    } 
    finally { setResetLoading(false); }
  };

  // 🚀 GOOGLE LOGIN — CON STATO LOADING E PROTEZIONE DOPPIO TAP iOS
  const handleGoogleLogin = async () => {
    if (googleLoading) return; // 🍎 Previene doppio tap su iOS
    try {
      setSignInError('');
      setGoogleLoading(true);
      const { error } = await signInWithGoogle();
      if (error) throw error;
    } catch (err) {
      setSignInError(mapSupabaseError(err.message || err.toString()));
      setGoogleLoading(false); 
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto rounded-3xl border border-slate-100 shadow-2xl transition-all duration-300">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#1B2A4A] to-[#2A3F6A]">
            {isResetting ? 'Reset Password' : 'Welcome'}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs tracking-wider text-slate-500">
            UMF Students Newspaper
          </DialogDescription>
        </DialogHeader>

        {/* 🚀 AVVISO MEDICO PER UTENTI iOS / SAFARI - GLASSMORPHISM WIDGET */}
        {!isResetting && (
          <div className="bg-gradient-to-br from-blue-50/80 to-indigo-50/50 border border-blue-200/50 text-blue-800 mt-2 p-3 rounded-2xl shadow-sm backdrop-blur-sm transition-all duration-300 hover:shadow-md flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
            <div className="text-xs leading-relaxed">
              <strong className="uppercase tracking-wider text-[10px] text-blue-600/80 font-bold block mb-1">🍎 iOS / Safari Advisory</strong>
              If login fails, Apple's "Hide IP Address" might be blocking it. <br/>
              <span className="inline-block mt-1 font-medium bg-white/60 px-2 py-0.5 rounded-full border border-blue-100">Fix: Use Chrome, or turn off 'Hide IP Address' in Safari settings.</span>
            </div>
          </div>
        )}

        {isResetting ? (
          // --- FORGOT PASSWORD VIEW ---
          <div className="space-y-4 pt-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {resetSuccess ? (
              <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800 rounded-2xl shadow-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <AlertDescription>Recovery link sent! Check your inbox.</AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {signInError && <Alert variant="destructive" className="rounded-2xl shadow-sm"><AlertCircle className="h-4 w-4" /><AlertDescription>{signInError}</AlertDescription></Alert>}
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="font-body text-sm font-semibold text-slate-700">Email Address</Label>
                  <Input id="reset-email" type="email" placeholder="student@umfcluj.ro" value={resetEmail} onChange={e => { setResetEmail(e.target.value); setSignInError(''); }} required className="rounded-xl border-slate-200 focus:ring-gold focus:border-gold transition-all duration-300 shadow-sm" />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#1B2A4A] to-[#2A3F6A] hover:from-[#2A3F6A] hover:to-[#1B2A4A] text-white rounded-xl py-6 font-bold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5" disabled={resetLoading}>
                  {resetLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Recovery Link'}
                </Button>
              </form>
            )}
            <Button variant="ghost" className="w-full text-sm font-body rounded-xl hover:bg-slate-100 transition-colors duration-300" onClick={() => setIsResetting(false)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sign In
            </Button>
          </div>
        ) : (
          // --- STANDARD LOGIN/SIGNUP VIEW ---
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <Tabs defaultValue="signin" className="mt-4">
              <TabsList className="grid w-full grid-cols-2 rounded-xl p-1 bg-slate-100 shadow-inner">
                <TabsTrigger value="signin" onClick={() => setSignInError('')} className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#1B2A4A] data-[state=active]:font-bold transition-all duration-300">Sign In</TabsTrigger>
                <TabsTrigger value="signup" onClick={() => setSignUpError('')} className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[#1B2A4A] data-[state=active]:font-bold transition-all duration-300">Sign Up</TabsTrigger>
              </TabsList>

              <div className="mt-6">
                {/* 🚀 BOTTONE GOOGLE CON LOADING STATE */}
                <Button
                  variant="outline"
                  className="w-full font-body font-bold mb-6 rounded-xl py-6 border-slate-200 hover:bg-slate-50 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                >
                  {googleLoading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin text-slate-500" />
                  ) : (
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  )}
                  {googleLoading ? 'Redirecting to Google…' : 'Continue with Google'}
                </Button>
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-4 text-slate-400 font-bold tracking-wider">Or continue with email</span></div>
                </div>
              </div>

              <TabsContent value="signin" className="mt-0">
                <form onSubmit={handleSignIn} className="space-y-4" noValidate>
                  {signInError && <Alert variant="destructive" className="rounded-2xl shadow-sm"><AlertCircle className="h-4 w-4" /><AlertDescription>{signInError}</AlertDescription></Alert>}
                  <div className="space-y-2">
                    <Label htmlFor="signin-email" className="font-body text-sm font-semibold text-slate-700">Email</Label>
                    <Input id="signin-email" type="email" value={signInData.email} onChange={e => { setSignInData(d => ({ ...d, email: e.target.value })); setSignInError(''); }} required className="rounded-xl border-slate-200 focus:ring-gold focus:border-gold transition-all duration-300 shadow-sm" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="signin-password" className="font-body text-sm font-semibold text-slate-700">Password</Label>
                      <button type="button" onClick={() => setIsResetting(true)} className="text-xs text-gold font-bold hover:text-yellow-600 transition-colors duration-300">
                        Forgot password?
                      </button>
                    </div>
                    <Input id="signin-password" type="password" value={signInData.password} onChange={e => { setSignInData(d => ({ ...d, password: e.target.value })); setSignInError(''); }} required className="rounded-xl border-slate-200 focus:ring-gold focus:border-gold transition-all duration-300 shadow-sm" />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-gold to-yellow-500 text-[#1B2A4A] hover:from-yellow-400 hover:to-yellow-500 font-bold py-6 rounded-xl transition-all duration-300 hover:shadow-[0_0_15px_rgba(250,204,21,0.5)] hover:-translate-y-0.5 mt-2" disabled={signInLoading}>
                    {signInLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="mt-0">
                <form onSubmit={handleSignUp} className="space-y-4" noValidate>
                  {signUpError && <Alert variant="destructive" className="rounded-2xl shadow-sm"><AlertCircle className="h-4 w-4" /><AlertDescription>{signUpError}</AlertDescription></Alert>}
                  {signUpSuccess && <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800 rounded-2xl shadow-sm"><CheckCircle2 className="h-4 w-4 text-emerald-500" /><AlertDescription>Account created! Check your inbox to confirm.</AlertDescription></Alert>}
                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="font-body text-sm font-semibold text-slate-700">Email</Label>
                    <Input id="signup-email" type="email" value={signUpData.email} onChange={e => { setSignUpData(d => ({ ...d, email: e.target.value })); setSignUpError(''); }} required disabled={signUpSuccess} className="rounded-xl border-slate-200 focus:ring-gold focus:border-gold transition-all duration-300 shadow-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="font-body text-sm font-semibold text-slate-700">Password</Label>
                    <Input id="signup-password" type="password" value={signUpData.password} onChange={e => { setSignUpData(d => ({ ...d, password: e.target.value })); setSignUpError(''); }} required minLength={6} disabled={signUpSuccess} className="rounded-xl border-slate-200 focus:ring-gold focus:border-gold transition-all duration-300 shadow-sm" />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-gold to-yellow-500 text-[#1B2A4A] hover:from-yellow-400 hover:to-yellow-500 font-bold py-6 rounded-xl transition-all duration-300 hover:shadow-[0_0_15px_rgba(250,204,21,0.5)] hover:-translate-y-0.5 mt-2" disabled={signUpLoading || signUpSuccess}>
                    {signUpLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
